// topoEditor/entities/link.js — "[Text](href)" becomes a non-clickable link span (href in the tooltip),
// with a hover-edit definition (change Text/href). Backend edit view = not clickable; frontend consumers
// can render the same convention as a real <a target="_blank"> via renderAnchorHtml().

import { escapeHtml } from "../render.js";

const LINK_PATTERN = /\[([^\]]*)\]\(([^)]*)\)/g;

// Toolbar icon (two chain links) — stroke:currentColor, same style as the OverlayToolbar SVGs.
const LINK_ICON = '<path d="M9 15l6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
    + '<path d="M11 6l1-1a4 4 0 0 1 6 6l-2 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    + '<path d="M13 18l-1 1a4 4 0 0 1-6-6l2-2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';

// German fallbacks only — the consumer passes translated labels (infoTextEditor.js).
const DEFAULT_LABELS = { title: "Link bearbeiten", button: "Link", textField: "Text:", urlField: "URL:" };

export function linkEntity({ labels = {} } = {}) {
    const text = { ...DEFAULT_LABELS, ...labels };
    return {
        kind: "link",
        pattern: LINK_PATTERN,
        parse: (m) => ({ text: m[1], href: m[2] }),
        renderHtml: (data, idx) => {
            const label = escapeHtml((data.text || "").trim() || data.href);
            return '<span class="topo2-editor-link" data-kind="link" data-idx="' + idx +
                '" title="' + escapeHtml(data.href) + '">' + label + "</span>";
        },
        // ── Nabe (topoDoc) edges: one place that knows all representations of a link ──
        // Tokens -> convention (normalizes the href so a token built from HTML/DB serializes to a valid string).
        serialize: (data) => "[" + (data.text ?? "") + "](" + normalizeHref(data.href) + ")",
        // Tokens -> frontend HTML (real clickable anchor, allow-listed href) — the headless output path.
        renderFrontendHtml: (data) => {
            // Empty text → icon-only anchor (the .topo2-link::after external icon is the whole content).
            // No href-as-label fallback: "[](href)" is intentionally text-less, not "show the URL".
            const label = escapeHtml((data.text || "").trim());
            const href = safeHref(data.href);
            // title = Ziel-URL: renderHtml und renderLiveHtml tun das längst, nur der echte Anchor nicht —
            // ausgerechnet dort, wo der Besucher vor dem Klick wissen will, wohin es geht (User 2026-09-23).
            if (href) { return '<a class="topo2-link" href="' + escapeHtml(href) + '" title="' + escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' + label + "</a>"; }
            return label;   // dirty scheme → inert plain text (empty text → nothing)
        },
        // Our editor HTML -> tokens (recognizes the TopoLiveEditor's <a> element, not arbitrary anchors).
        matchElement: (el) =>
            (el.nodeName === "A" && el.classList && el.classList.contains("topo2-live-link"))
                ? { text: el.textContent, href: (el.dataset && el.dataset.href) || "" }
                : null,
        // Tokens -> contenteditable form: the editable <a> the live editor authors (text is the editable label,
        // href lives in data-href). data-kind lets the editor address it generically.
        renderLiveHtml: (data) =>
            '<a class="topo2-editor-link topo2-live-link" data-kind="link" data-href="' + escapeHtml(data.href || "") +
            '" title="' + escapeHtml(data.href || "") + '">' + escapeHtml(data.text ?? "") + "</a>",
        edit: {
            trigger: "hover",
            title: text.title,
            label: text.button,                                   // selection-toolbar button (fallback text)
            icon: LINK_ICON,                                      // preferred: square icon button
            fields: [
                // Beschriftungen mit Doppelpunkt, und "URL" statt des technischen "href" (User 2026-09-23).
                { key: "text", label: text.textField },
                { key: "href", label: text.urlField },
            ],
            canCreate: (selectedText) => (selectedText || "").trim().length > 0,   // any non-empty selection
            newData: (selectedText) => ({ text: selectedText, href: "" }),   // wrap a selection into a href-less link
            normalize: (values) => ({ text: values.text, href: normalizeHref(values.href) }),  // store a valid (schemed) href
            format: (data) => "[" + data.text + "](" + normalizeHref(data.href) + ")",
            create: (selectedText) => "[" + selectedText + "]()",  // convention form (used by the preview editor)
            remove: (data) => data.text,                           // unwrap: [text](href) -> text
        },
    };
}

// Security + convenience: decide the href that actually goes into a public anchor.
//   • http/https/mailto            → used as-is
//   • any OTHER explicit scheme     → rejected (javascript:, data:, … render inert) — info_text is
//                                     admin-entered but publicly displayed
//   • relative "/…" or anchor "#…"  → rejected (not an external link)
//   • scheme-less (www.x, topo.at)  → assume https:// (so users need not type the scheme)
export function safeHref(href) {
    const h = (href || "").trim();
    if (!h) { return null; }
    if (/^(https?:|mailto:)/i.test(h)) { return h; }
    if (/^[a-z][a-z0-9+.\-]*:/i.test(h)) { return null; }
    if (h.startsWith("/") || h.startsWith("#")) { return null; }
    return "https://" + h;
}

// Save-time normalization: give a scheme-less href an https:// prefix so the STORED value is valid data
// ("topo.at" -> "https://topo.at"). Explicit schemes and relative paths are kept as typed.
export function normalizeHref(href) {
    const h = (href || "").trim();
    if (!h) { return h; }
    if (/^[a-z][a-z0-9+.\-]*:/i.test(h) || h.startsWith("/") || h.startsWith("#")) { return h; }
    return "https://" + h;
}

// Frontend render: the same plain-text convention as real clickable anchors (new tab). Not used by the
// editor itself — for consumers that display stored info text on the public site. Text is escaped; only
// allow-listed hrefs produce an <a class="topo2-link">, otherwise the label is shown as plain (inert)
// text and a short console warning flags the dirty link in the DB.
export function renderLinksAsAnchors(text) {
    let out = "", last = 0, m;
    LINK_PATTERN.lastIndex = 0;
    while ((m = LINK_PATTERN.exec(text))) {
        out += escapeHtml(text.slice(last, m.index));
        const label = escapeHtml((m[1] || "").trim());   // empty text → icon-only (no href-as-label fallback), see renderFrontendHtml
        const href = safeHref(m[2]);
        if (href) {
            out += '<a class="topo2-link" href="' + escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' + label + "</a>";
        } else {
            console.warn("[topoEditor] dirty link href in info_text, rendered as plain text:", m[2]);
            out += label;
        }
        last = m.index + m[0].length;
    }
    out += escapeHtml(text.slice(last));
    return out.replace(/\n/g, "<br>");
}
